import { Injectable, makeStateKey, TransferState } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { Observable, of, tap } from "rxjs";
import { ContactForm } from "../interfaces/contact.interface";
import { Subscriber } from "../interfaces/subscribers.interface";
import { Consultation } from "../interfaces/consultation.interface";

@Injectable({
  providedIn: 'root'
})
export class PublicService {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
  ) {
  }

  setTransferState(key: any, data: any) {
    this.transferState.set(makeStateKey<any>(key), data);
  }

  getTransferState(key: any) {
    return this.transferState.get(makeStateKey<any>(key), null);
  }

  getDataByTransferStateUrl(url: string) {
    const transferState = this.getTransferState(url);

    if (this.getTransferState(url)) {
      return of(transferState)
    } else {
      return this.http.get<any>(url).pipe(
        tap((res: any) => {
          this.setTransferState(url, res)
        })
      );
    }
  }

  sendContacts(contacts: ContactForm) {
    const { name, surname, email, phone, services, selectedService } = contacts;
    return this.http.post<any>('api/public/crud/contacts', { data: [{ name }, { surname }, { email }, { phone }, { services: services.map((service) => (service as unknown as { [key: string]: string })?.['ro']).toString() }, { selected_service: (selectedService as unknown as { [key: string]: string })?.['ro'] }] });
  }

  sendSubscribers(subscriber: Subscriber) {
    const { email } = subscriber;
    return this.http.post<any>('api/public/crud/subscribers', { data: [{ email }] });
  }

  sendConsultation(consultation: Consultation) {
    const data = [
      { name: consultation.name },
      { surname: consultation.surname },
      { email: consultation.email },
      { phone_number: consultation.phoneNumber },
      // {vehicle_make: consultation.vehicleMake},
      // {vehicle_model: consultation.vehicleModel},
      // {year: consultation.year},
      // {win_code: consultation.winCode},
      // {comment: consultation.comment},
      {
        service_categories: consultation.serviceCategories.map(({ category, services }) => (
          {
            category: (category as unknown as { [key: string]: string })?.['ro'],
            services: services.map((service) => (service as unknown as { [key: string]: string })?.['ro']).toString()
          }
        ))
      },
    ];

    return this.http.post<any>('api/crud/consultations', { data });
  }

  getLanguages(params?: any) {
    const url = 'api/public/languages';

    if (params) {
      return this.http.get<any>(url, { params });
    } else {
      return this.getDataByTransferStateUrl(url);
    }
  }

  getAnalytics(): Observable<any> {
    const url = 'api/public/analytics';
    return this.getDataByTransferStateUrl(url);
  }

  getReviews(params?: any) {
    const url = 'api/public/crud/reviews';

    if (params)
      return this.http.get<any>(url, {params});

    return this.getDataByTransferStateUrl(url);
  }

  getNetworks(params?: any) {
    const url = 'api/public/crud/social_networks';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getFAQ(params?: any) {
    const url = 'api/public/crud/faq';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getMeta(params: any) {
    return this.http.get<any>('api/public/crud/meta', { params });
  }

  getStaticPages(params?: any) {
    if (params) {
      return this.http.get<any>('api/public/crud/static_pages', { params });
    } else {
      const url = 'api/public/crud/static_pages';
      return this.getDataByTransferStateUrl(url);
    }
  }

  getCompanyDetails(params: any) {
    const url = 'api/public/crud/company_details';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getOffers(params?: any) {
    const url = 'api/public/crud/offers';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getAdvantages(params?: any) {
    const url = 'api/public/crud/advantages';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getTeam(params?: any) {
    const url = 'api/public/crud/team';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getTravel(params: any) {
    const url = 'api/public/crud/travel';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getServiceCategories(params?: any) {
    const url = 'api/public/crud/service_categories';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getServiceGroups(params?: any) {
    const url = 'api/public/crud/service_groups';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getLocations(params?: any) {
    const url = 'api/public/crud/locations';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getMetrics(params?: any) {
    const url = 'api/public/crud/metrics';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getServices(params?: any) {
    const url = 'api/public/crud/services';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getServiceById(id: number | string) {
    const url = `api/public/crud/services/${id}`;

    return this.getDataByTransferStateUrl(url);
  }

  getServiceBySlug(slug: string) {
    const url = 'api/public/crud/services';
    const params = {filter: `url_equals_${slug}`};

    return this.http.get<any>(url, {params});
  }

  getBlogCategories(params?: any) {
    const url = 'api/public/crud/blog_categories';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getBlogs(params?: any) {
    const url = 'api/public/crud/blogs';

    if (params)
      return this.http.get<any>(url, { params });

    return this.getDataByTransferStateUrl(url);
  }

  getBlogById(id: number | string) {
    const url = `api/public/crud/blogs/${id}`;

    return this.getDataByTransferStateUrl(url);
  }

  getBlogBySlug(slug: string) {
    const url = 'api/public/crud/blogs';
    const params = {filter: `url_equals_${slug}`};

    return this.http.get<any>(url, {params});
  }

  downloadPdf(url: string) {
    return this.http.get(url, { responseType: 'blob' });
  }
}
